import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { useCreateUserMutation } from '@crehana/auth';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { PrimaryButton, TextField } from '@crehana/ui';
import { useTrackingContext } from '@crehana/web/ReactContexts/TrackingContext';
import { Controller, useForm } from 'react-hook-form/dist/index.ie11';

import { validations } from '../constants';
import LegalCheckbox, {
  LegalErrorMessage,
  rulesForRHFControllerComponent,
} from '../LegalCheckbox/LegalCheckbox';
import type { AuthInnerFormProps } from '../types';
import {
  getNextUrl,
  inIframe,
  sendRegisterTrackToGoogleAdsAndAnalytics,
} from '../utils';
import AUTH_PAGES_VERIFY_USER_QUERY from './graphql/AuthFormVerifyUserQuery.graphql';
import type {
  AuthFormVerifyUserQuery,
  AuthFormVerifyUserQueryVariables,
} from './graphql/types/AuthFormVerifyUserQuery';

const SignUpForm: React.FC<React.PropsWithChildren<AuthInnerFormProps>> = ({
  referFriendId,
  onCustomSuccess,
  emailDefault,
  isDark,
  removeAutofocus,
  customLabelCta,
  jobToBeDoneData,
  renderLegalCheckbox = true,
  onChangeForm,
}) => {
  const { t } = useTranslation();
  const { formattedIntent, clearIntent } = useTrackingContext();
  const nextUrl = getNextUrl();

  const { refetch: validateExistedEmail } = useQuery<
    AuthFormVerifyUserQuery,
    AuthFormVerifyUserQueryVariables
  >(AUTH_PAGES_VERIFY_USER_QUERY, {
    skip: true,
  });
  const [createUserMutation] = useCreateUserMutation();

  const { handleSubmit, errors, control, formState, trigger, setError } =
    useForm({
      mode: 'onChange',
      defaultValues: {
        email: emailDefault || '',
        password: '',
        checkboxLegal: true,
      },
    });

  const validateEmail = async (value: string) => {
    const { data } = await validateExistedEmail({ value });

    if (data) {
      const { messages, success } = data?.verifyUser || {};

      // biome-ignore lint/style/useBlockStatements: legacy code - style/useBlockStatements
      if (success) return true;

      const [first] = messages || [];

      if (first) {
        return first.message as string;
      }
      return undefined;
    }
  };

  const onSubmit = handleSubmit(async data => {
    const isInIframe = inIframe();

    const res = await createUserMutation({
      variables: {
        ...data,
        nextUrl,
        referFriendId: referFriendId?.toString(),
        // JobToBeDone data
        ...(jobToBeDoneData || formattedIntent),
      },
    });

    const { messages, success, redirect, user } = res?.data?.createUser || {};

    if (!success) {
      const [first] = messages || [];

      if (first?.message) {
        setError((first.field as 'email' | 'password') || 'email', {
          type: '',
          message: first.message,
        });
      }

      return;
    }

    sendRegisterTrackToGoogleAdsAndAnalytics();

    if (!isInIframe) {
      if (onCustomSuccess && user) {
        // TODO add types to user
        onCustomSuccess({
          user,
          redirect,
          isNewUser: true,
        });
      } else {
        window.location.href = redirect!;
      }
    } else if (onCustomSuccess && user) {
      onCustomSuccess({
        user,
        redirect,
        isNewUser: true,
      });
    } else {
      window.location.href = redirect!;
    }
  });

  const onLogin = () => {
    window.location.href = `/entrar/${
      window.location.pathname !== 'entrar'
        ? `?next=${window.location.href}`
        : ''
    }`;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code - correctness/useExhaustiveDependencies
  useEffect(() => {
    if (emailDefault) {
      trigger('email');
    }
  }, []);

  // We clear the "Intent (Job to be done) data" when the component gets unmounted
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code - correctness/useExhaustiveDependencies
  useEffect(() => {
    return clearIntent;
  }, []);

  const { isSubmitting, isValid } = formState;
  const isDisabled = isSubmitting || !isValid;

  return (
    <>
      <form onSubmit={onSubmit}>
        <div>
          <Controller
            control={control}
            name="email"
            rules={{
              required: validations.required(t),
              pattern: validations.email(t),
              validate: validateEmail,
            }}
            render={({ onBlur, onChange, value }) => (
              <TextField
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                autoFocus={!removeAutofocus}
                full
                type="email"
                id="email"
                maxLength={50}
                label={t('EMAIL', { defaultValue: 'Correo electrónico' })}
                disabled={isSubmitting}
                data-testid="email-field"
                darkMode={isDark}
                className="mb-20"
                error={!!errors.email}
                errorText={
                  errors.email?.message ? errors.email?.message : undefined
                }
              />
            )}
          />
        </div>
        <div className="w-full">
          <Controller
            control={control}
            name="password"
            rules={{
              required: validations.required(t),
              minLength: validations.minLength(t),
              maxLength: validations.maxLength(t),
            }}
            render={({ onBlur, onChange, value }) => (
              <TextField
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                full
                label={t('PASSWORD', { defaultValue: 'Contraseña' })}
                type="password"
                id="password"
                maxLength={20}
                disabled={isSubmitting}
                data-testid="password-field"
                darkMode={isDark}
                className="mb-20"
                error={!!errors.password}
                errorText={
                  errors.password?.message
                    ? t(errors.password?.message, {
                        defaultValue:
                          'Tu contraseña debe de tener al menos 6 caracteres',
                      })
                    : undefined
                }
              />
            )}
          />
        </div>
        {renderLegalCheckbox && (
          <div className="mb-20">
            <Controller
              control={control}
              name="checkboxLegal"
              render={({ onChange, value }) => (
                <LegalCheckbox
                  darkMode={isDark}
                  dataTestId="legal-field"
                  onChange={onChange}
                  value={value}
                />
              )}
              rules={rulesForRHFControllerComponent(t)}
            />
            {errors.checkboxLegal?.message && (
              <LegalErrorMessage message={errors.checkboxLegal.message} />
            )}
          </div>
        )}
        <div>
          <PrimaryButton
            className="w-full"
            label={
              customLabelCta || t('REGISTER', { defaultValue: 'Regístrate' })
            }
            type="submit"
            data-testid="submit-button"
            data-cy="submit"
            size="l"
            darkMode={isDark}
            disabled={isDisabled}
          />
        </div>
      </form>
      {!onChangeForm && (
        <div className="mt-16 text-center">
          <p className={`font-body4 ${isDark ? 'text-white' : ''}`}>
            {t('DO_YOU_HAVE_A_ACCOUNT', {
              defaultValue: '¿Ya tienes una cuenta?',
            })}{' '}
            <button
              onClick={onLogin}
              type="button"
              className="font-link-2 text-secondary-main"
            >
              {t('SIGN_IN', {
                defaultValue: 'Inicia sesión',
              })}
            </button>
          </p>
        </div>
      )}
    </>
  );
};

export default SignUpForm;
